import React, { useEffect, useRef, useState } from "react";
import "./sales-site.css";
import NailedLogo from "./components/NailedLogo";
import { billingLabel, formatCurrency, hydrateFallbackPlans, plansFromCatalog, priceForPlan, pricePresentation, pricingPeriods, resolveSalesApiBase } from "./pricingCatalog";

const apiBaseUrl = resolveSalesApiBase(process.env.REACT_APP_BASE_URL_SALES, typeof window === "undefined" ? "" : window.location.hostname);

/**
 * Lightweight analytics dispatcher. No analytics SDK is currently wired
 * into the sales site, so events are pushed to the GTM-style dataLayer
 * (ready for a future container) and forwarded to any provider that is
 * present on window (gtag / Segment). Safe no-op when none exist.
 */
function track(event, props = {}) {
  const payload = { event, ...props };
  try {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    if (typeof window.gtag === "function") window.gtag("event", event, props);
    if (window.analytics && typeof window.analytics.track === "function") window.analytics.track(event, props);
  } catch (e) {
    /* analytics must never break the page */
  }
}

/* ------------------------------------------------------------------ */
/* Inline icons (JSX-safe)                                             */
/* ------------------------------------------------------------------ */
const svgBase = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round" };
const ArrowRight = (p) => (<svg {...svgBase} strokeWidth={2.2} {...p}><path d="M5 12h14M13 6l6 6-6 6" /></svg>);
const Check = (p) => (<svg {...svgBase} strokeWidth={2.4} {...p}><path d="M20 6 9 17l-5-5" /></svg>);
const Layers = (p) => (<svg {...svgBase} strokeWidth={2} {...p}><path d="M12 2 2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>);
const PhoneIc = (p) => (<svg {...svgBase} strokeWidth={2} {...p}><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2 4.2 2 2 0 0 1 4 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L7.6 9.8a16 16 0 0 0 6 6l1.4-1.5a2 2 0 0 1 2.1-.4c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2z" /></svg>);
const Pin = (p) => (<svg {...svgBase} strokeWidth={2} {...p}><path d="M21 10c0 6-9 12-9 12s-9-6-9-12a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>);
const Sun = (p) => (<svg {...svgBase} strokeWidth={2} {...p}><circle cx="12" cy="12" r="4.5" /><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" /></svg>);
const Moon = (p) => (<svg {...svgBase} strokeWidth={2} {...p}><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>);
const MenuIc = (p) => (<svg {...svgBase} strokeWidth={2} {...p}><path d="M3 6h18M3 12h18M3 18h18" /></svg>);
const CloseIc = (p) => (<svg {...svgBase} strokeWidth={2} {...p}><path d="M18 6 6 18M6 6l12 12" /></svg>);

/* ------------------------------------------------------------------ */
/* Bespoke pillar illustrations (theme-aware line-art)                 */
/* ------------------------------------------------------------------ */
const IntakeArt = () => (
  <svg viewBox="0 0 220 150" fill="none" aria-hidden="true">
    <g className="art-el">
      <rect x="80" y="32" width="82" height="100" rx="6" fill="var(--panel)" stroke="var(--line-strong)" strokeWidth="1.5" transform="rotate(4 121 82)" />
      <rect x="64" y="26" width="82" height="100" rx="6" fill="var(--panel)" stroke="var(--ink-soft)" strokeWidth="1.8" />
      <rect x="76" y="40" width="44" height="7" rx="3.5" fill="var(--accent)" />
      <g stroke="var(--line-strong)" strokeWidth="3" strokeLinecap="round">
        <path d="M76 60 H134" /><path d="M76 72 H134" /><path d="M76 84 H116" />
      </g>
      <g stroke="var(--slate-2)" strokeWidth="1.5">
        <rect x="76" y="98" width="58" height="20" rx="2" /><path d="M105 98 V118" />
      </g>
      <circle cx="150" cy="116" r="16" fill="var(--accent)" />
      <path d="M150 124 V108 M144 114 l6 -6 6 6" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  </svg>
);
const FieldArt = () => (
  <svg viewBox="0 0 220 150" fill="none" aria-hidden="true">
    <g className="art-el">
      <rect x="40" y="34" width="140" height="86" rx="6" fill="var(--panel)" stroke="var(--ink-soft)" strokeWidth="1.8" />
      <g stroke="var(--line-strong)" strokeWidth="1.3">
        <path d="M40 74 H118" /><path d="M118 34 V120" /><path d="M118 92 H180" />
      </g>
      <path d="M64 102 C 90 98, 96 60, 130 56" stroke="var(--accent)" strokeWidth="2" strokeDasharray="4 6" className="art-beam" />
      <circle cx="64" cy="102" r="5" fill="var(--panel)" stroke="var(--accent)" strokeWidth="2" />
      <circle cx="132" cy="46" r="12" fill="var(--accent)" />
      <path d="M123 53 L132 68 L141 53 Z" fill="var(--accent)" />
      <circle cx="132" cy="46" r="4.6" fill="#fff" />
    </g>
  </svg>
);
const FinanceArt = () => (
  <svg viewBox="0 0 220 150" fill="none" aria-hidden="true">
    <g className="art-el">
      <rect x="44" y="28" width="134" height="96" rx="8" fill="var(--panel)" stroke="var(--ink-soft)" strokeWidth="1.8" />
      <rect x="58" y="40" width="42" height="6" rx="3" fill="var(--line-strong)" />
      <rect x="140" y="37" width="26" height="12" rx="4" fill="var(--accent)" />
      <g>
        <rect x="62" y="88" width="12" height="22" rx="2" fill="var(--line-strong)" />
        <rect x="82" y="78" width="12" height="32" rx="2" fill="var(--line-strong)" />
        <rect x="102" y="66" width="12" height="44" rx="2" fill="var(--accent)" />
        <rect x="122" y="82" width="12" height="28" rx="2" fill="var(--line-strong)" />
      </g>
      <path d="M68 86 L88 78 L108 62 L128 76" stroke="var(--accent)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="152" cy="98" r="15" fill="#1f9d55" />
      <path d="M145 98 l5 5 9 -10" stroke="#fff" strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  </svg>
);

const NAV_LINKS = [
  { href: "#platform", label: "Platform" },
  { href: "#use-cases", label: "Use Cases" },
  { href: "#pricing", label: "Pricing" },
  { href: "#mobile", label: "Mobile Apps" },
  { label: "Tour", tour: true },
  { href: "#contact", label: "Contact" },
];

/* ------------------------------------------------------------------ */
/* Use-case icons                                                      */
/* ------------------------------------------------------------------ */
const House = (p) => (<svg {...svgBase} strokeWidth={1.9} {...p}><path d="M3 10.5 12 3l9 7.5" /><path d="M5.5 9.5V21h13V9.5" /><path d="M10 21v-6h4v6" /></svg>);
const Wrench = (p) => (<svg {...svgBase} strokeWidth={1.9} {...p}><path d="M14.6 6.3a3.8 3.8 0 0 0-5 5L4 16.9V20h3.1l5.6-5.6a3.8 3.8 0 0 0 5-5l-2.7 2.7-2-2 2.6-2.8z" /></svg>);
const Building = (p) => (<svg {...svgBase} strokeWidth={1.9} {...p}><rect x="4.5" y="3" width="15" height="18" rx="1" /><path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2" /><path d="M10 21v-3h4v3" /></svg>);
const HardHat = (p) => (<svg {...svgBase} strokeWidth={1.9} {...p}><path d="M4 16a8 8 0 0 1 16 0" /><path d="M10 8.2V5.2A1.2 1.2 0 0 1 11.2 4h1.6A1.2 1.2 0 0 1 14 5.2v3" /><path d="M2.5 16h19" /></svg>);
const Skyline = (p) => (<svg {...svgBase} strokeWidth={1.9} {...p}><path d="M3 21V9h7v12" /><path d="M10 21V4h11v17" /><path d="M6 12v0M6 15v0M6 18v0M14 8v0M14 12v0M14 16v0M18 8v0M18 12v0M18 16v0" /></svg>);
const Clipboard = (p) => (<svg {...svgBase} strokeWidth={1.9} {...p}><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1H9z" /><path d="M8.5 11.5 10 13l2.5-3M8.5 16.5h6" /></svg>);

const USE_CASES = [
  { icon: <House />, title: "Residential builds", body: "New homes from foundation to handover, with every stage documented and shared." },
  { icon: <Wrench />, title: "Renovations & remodels", body: "Track scope, change orders and client sign-off as the work evolves on site." },
  { icon: <Building />, title: "Commercial construction", body: "Fit-outs and larger multi-team projects coordinated in one connected workflow." },
  { icon: <HardHat />, title: "Specialist subcontracting", body: "Bid the work, manage job-site attendance, upload proof and get paid on time." },
  { icon: <Skyline />, title: "Property developers", body: "Oversee multiple projects, budgets and stakeholders from a single view." },
  { icon: <Clipboard />, title: "General contractors", body: "Run estimates, subcontractors, claims and invoices from one admin dashboard." },
];

/* ------------------------------------------------------------------ */
/* Pricing data — features verified against the current product.       */
/* (Omitted as unbuilt: audit history, advanced/portfolio reporting,   */
/*  workflow automations.)                                             */
/* ------------------------------------------------------------------ */
const PLANS = [
  {
    id: "essentials",
    name: "Essentials",
    popular: false,
    startingAt: false,
    audience: "Small contractors moving away from spreadsheets, WhatsApp, texts, and disconnected tools.",
    monthly: 149,
    annualMonthly: 119,
    annualTotal: 1428,
    onboarding: 499,
    limits: ["3 internal team members", "10 active projects", "Unlimited clients", "Unlimited subcontractor collaborators"],
    features: [
      "Project dashboard",
      "Site photo & document records",
      "Estimates & proposals",
      "Client portal",
      "Client approvals",
      "Daily project logs",
      "Invoices & payment requests",
      "Standard email support",
    ],
    ctaLabel: "Start with Essentials",
    ctaEvent: "plan_selected",
  },
  {
    id: "growth",
    name: "Growth",
    popular: true,
    startingAt: false,
    audience: "Established contractors managing several projects, crews, clients, and change orders.",
    monthly: 299,
    annualMonthly: 239,
    annualTotal: 2868,
    onboarding: 999,
    limits: ["10 internal team members", "Unlimited active projects", "Unlimited clients", "Unlimited subcontractor collaborators"],
    features: [
      "Everything in Essentials",
      "Change orders & contract revisions",
      "Electronic signatures & approvals",
      "Scheduling & team assignments",
      "Budget & job-cost tracking",
      "Branded client experience",
      "Workflow notifications",
      "Priority support",
    ],
    ctaLabel: "Start with Growth",
    ctaEvent: "plan_selected",
  },
  {
    id: "scale",
    name: "Scale",
    popular: false,
    startingAt: true,
    audience: "Larger contractors, multi-team operators, and construction businesses requiring advanced controls and implementation support.",
    monthly: 699,
    annualMonthly: 559,
    annualTotal: 6708,
    onboarding: 2500,
    limits: ["Up to 25 internal team members", "Multiple teams or branches", "Unlimited active projects", "Unlimited clients & subcontractors"],
    features: [
      "Everything in Growth",
      "Multiple business locations",
      "Advanced roles & permissions",
      "Custom integrations & API access",
      "Data migration assistance",
      "Custom branding",
      "Dedicated onboarding contact",
      "Priority response agreement",
    ],
    ctaLabel: "Start with Scale",
    ctaEvent: "plan_selected",
  },
];

const COMPARE = [
  { label: "Internal team members", values: ["3", "10", "Up to 25"] },
  { label: "Active projects", values: ["10", "Unlimited", "Unlimited"] },
  { label: "Client & subcontractor collaborators", values: ["Unlimited", "Unlimited", "Unlimited"] },
  { label: "Site photo & document records", values: [true, true, true] },
  { label: "Estimates & proposals", values: [true, true, true] },
  { label: "Client portal", values: [true, true, true] },
  { label: "Client approvals", values: [true, true, true] },
  { label: "Electronic signatures", values: [false, true, true] },
  { label: "Change orders & revisions", values: [false, true, true] },
  { label: "Daily project logs", values: [true, true, true] },
  { label: "Scheduling & team assignments", values: [false, true, true] },
  { label: "Invoices & payment requests", values: [true, true, true] },
  { label: "Budget & job-cost tracking", values: [false, true, true] },
  { label: "Branded client experience", values: [false, true, true] },
  { label: "Advanced roles & permissions", values: [false, false, true] },
  { label: "Multiple business locations", values: [false, false, true] },
  { label: "Custom integrations & API access", values: [false, false, true] },
  { label: "Data migration assistance", values: [false, false, true] },
  { label: "Support", values: ["Standard email", "Priority", "Priority + response agreement"] },
];

const FAQS = [
  {
    q: "Are clients and subcontractors charged as users?",
    a: "No. Pricing is based on your internal company team. Invited clients and subcontractor collaborators are included within the limits of your plan.",
  },
  {
    q: "Why is there an onboarding fee?",
    a: "Nailed is configured around your company and workflow. Onboarding covers account setup, team configuration, template preparation, and guided implementation.",
  },
  {
    q: "Can I change plans later?",
    a: "Yes. Companies can move to a larger plan as their team, project volume, or operational requirements increase.",
  },
  {
    q: "Can I pay monthly?",
    a: "Available billing periods are shown in the pricing section and are maintained by our billing team in the live Nailed catalogue.",
  },
  {
    q: "Are payment-processing fees included?",
    a: "No. Payments collected through Nailed are handled by a third-party payment processor, and its processing fees are separate from your Nailed subscription.",
  },
  {
    q: "Can Nailed migrate my existing project information?",
    a: "Our team provides guided migration assistance on Scale plans, helping import your existing project information as part of onboarding. Nailed does not offer fully automated self-serve migration.",
  },
  {
    q: "Can I cancel?",
    a: "Yes. Cancellation follows the terms in your Nailed service agreement — contact your onboarding representative or our team to cancel, and review Nailed’s Terms of Service for the exact terms that apply to your plan.",
  },
];

const fmt = (n, currency = "USD") => formatCurrency(n, currency);

/* ------------------------------------------------------------------ */
/* Product tour visuals (theme-aware, brand-consistent)                */
/* ------------------------------------------------------------------ */
const TourCapture = () => (
  <svg viewBox="0 0 360 260" fill="none" aria-hidden="true">
    <rect x="8" y="8" width="344" height="244" rx="16" fill="var(--panel)" stroke="var(--line)" />
    <g fontFamily="IBM Plex Mono" fontSize="10" letterSpacing="1.5">
      <circle cx="30" cy="34" r="4" fill="var(--accent)" />
      <text x="42" y="38" fill="var(--slate-2)">ESX · SITE CAPTURE</text>
      <text x="300" y="38" fill="var(--slate-2)">A-01</text>
    </g>
    <g transform="translate(60 58)">
      <rect x="0" y="0" width="240" height="150" rx="2" fill="none" stroke="var(--ink)" strokeWidth="2.2" />
      <path d="M135 0 V96" stroke="var(--ink-soft)" strokeWidth="1.6" />
      <path d="M0 96 H135" stroke="var(--ink-soft)" strokeWidth="1.6" />
      <path d="M135 62 H240" stroke="var(--ink-soft)" strokeWidth="1.6" />
      <path d="M80 96 A20 20 0 0 1 100 116" stroke="var(--accent)" strokeWidth="1.6" />
      <rect x="18" y="18" width="34" height="22" rx="2" stroke="var(--slate-2)" strokeWidth="1.3" />
      <circle cx="205" cy="34" r="13" stroke="var(--slate-2)" strokeWidth="1.3" />
      <text x="34" y="80" fontFamily="IBM Plex Mono" fontSize="8" fill="var(--slate-2)">LIVING</text>
      <text x="168" y="40" fontFamily="IBM Plex Mono" fontSize="8" fill="var(--slate-2)">KITCHEN</text>
      <rect x="0" y="96" width="240" height="2.5" fill="var(--accent)" />
      <rect x="0" y="98.5" width="240" height="18" fill="url(#tourScan)" />
      <path d="M0 14 V0 H14 M240 14 V0 H226" stroke="var(--accent)" strokeWidth="2" fill="none" />
    </g>
    <defs>
      <linearGradient id="tourScan" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="var(--accent)" stopOpacity="0.45" />
        <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
      </linearGradient>
    </defs>
  </svg>
);
const TourDash = () => (
  <svg viewBox="0 0 360 260" fill="none" aria-hidden="true">
    <rect x="8" y="8" width="344" height="244" rx="16" fill="var(--panel)" stroke="var(--line)" />
    <g>
      <circle cx="28" cy="30" r="4" fill="var(--line-strong)" />
      <circle cx="42" cy="30" r="4" fill="var(--line-strong)" />
      <circle cx="56" cy="30" r="4" fill="var(--line-strong)" />
      <text x="76" y="34" fontFamily="IBM Plex Mono" fontSize="10" fill="var(--slate-2)">nailed.app / dashboard</text>
      <path d="M8 46 H352" stroke="var(--line)" />
    </g>
    {[["18", "ACTIVE JOBS", 24], ["$240k", "INVOICED", 128], ["7", "OPEN BIDS", 232]].map(([n, l, x]) => (
      <g key={l} transform={`translate(${x} 60)`}>
        <rect width="104" height="52" rx="8" fill="var(--paper)" stroke="var(--line)" />
        <text x="12" y="26" fontFamily="Archivo" fontWeight="800" fontSize="18" fill="var(--ink)">{n}</text>
        <text x="12" y="42" fontFamily="IBM Plex Mono" fontSize="7.5" letterSpacing="0.5" fill="var(--slate-2)">{l}</text>
      </g>
    ))}
    {[["LV", "Lakeview Renovation", "ON TRACK", 0.82, 128], ["RH", "Ridge House Build", "PAID", 1, 186]].map(([av, t, s, w, y]) => (
      <g key={t} transform={`translate(24 ${y})`}>
        <rect width="312" height="46" rx="8" fill="var(--paper)" stroke="var(--line)" />
        <rect x="12" y="11" width="24" height="24" rx="6" fill="var(--accent-tint)" />
        <text x="24" y="27" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="9" fill="var(--accent)">{av}</text>
        <text x="46" y="22" fontFamily="Archivo" fontWeight="600" fontSize="11" fill="var(--ink)">{t}</text>
        <rect x="46" y="30" width="150" height="4" rx="2" fill="var(--line)" />
        <rect x="46" y="30" width={150 * w} height="4" rx="2" fill="var(--accent)" />
        <rect x="242" y="14" width="58" height="18" rx="9" fill="color-mix(in srgb,#1f9d55 16%,transparent)" />
        <text x="271" y="26" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8" fill="#1f9d55">{s}</text>
      </g>
    ))}
  </svg>
);
const TourApps = () => (
  <svg viewBox="0 0 360 260" fill="none" aria-hidden="true">
    <rect x="112" y="10" width="136" height="240" rx="26" fill="var(--block)" />
    <rect x="120" y="18" width="120" height="224" rx="18" fill="var(--paper-2)" />
    <rect x="120" y="18" width="120" height="46" rx="18" fill="var(--block)" />
    <text x="134" y="40" fontFamily="IBM Plex Mono" fontSize="8" letterSpacing="1" fill="#8a93a3">CLIENT · RIDGE HOUSE</text>
    <text x="134" y="55" fontFamily="Archivo" fontWeight="700" fontSize="12" fill="#fff">Your updates</text>
    <g transform="translate(130 74)">
      <rect width="100" height="46" rx="8" fill="var(--paper)" stroke="var(--line)" />
      <text x="10" y="19" fontFamily="Archivo" fontWeight="600" fontSize="9" fill="var(--ink)">Foundation complete</text>
      <rect x="10" y="26" width="80" height="12" rx="3" fill="var(--accent-tint)" />
    </g>
    <g transform="translate(130 128)">
      <rect width="100" height="40" rx="8" fill="var(--paper)" stroke="var(--line)" />
      <text x="10" y="18" fontFamily="Archivo" fontWeight="600" fontSize="9" fill="var(--ink)">Invoice #0421</text>
      <rect x="66" y="9" width="26" height="13" rx="6" fill="color-mix(in srgb,#1f9d55 18%,transparent)" />
      <text x="79" y="18" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill="#1f9d55">PAID</text>
      <text x="10" y="31" fontFamily="IBM Plex Mono" fontSize="7.5" fill="var(--slate-2)">$12,400</text>
    </g>
    <g transform="translate(130 176)">
      <rect width="100" height="30" rx="9" fill="var(--accent)" />
      <text x="50" y="20" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="9" letterSpacing="1" fill="#fff">APPROVE</text>
    </g>
  </svg>
);
const TourContract = () => (
  <svg viewBox="0 0 360 260" fill="none" aria-hidden="true">
    <rect x="70" y="18" width="220" height="224" rx="12" fill="var(--panel)" stroke="var(--line)" />
    <text x="90" y="48" fontFamily="IBM Plex Mono" fontSize="9" letterSpacing="1" fill="var(--slate-2)">CONTRACT · RIDGE HOUSE</text>
    <rect x="230" y="36" width="46" height="16" rx="8" fill="var(--accent-tint)" />
    <text x="253" y="47" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill="var(--accent-deep)">CHANGE</text>
    <g stroke="var(--line-strong)" strokeWidth="3" strokeLinecap="round">
      <path d="M90 70 H270" /><path d="M90 84 H270" /><path d="M90 98 H240" /><path d="M90 112 H260" /><path d="M90 126 H220" />
    </g>
    <rect x="90" y="150" width="180" height="70" rx="8" fill="var(--paper)" stroke="var(--line)" />
    <text x="102" y="170" fontFamily="IBM Plex Mono" fontSize="8" letterSpacing="1" fill="var(--slate-2)">SIGNATURE</text>
    <path d="M104 200 c 14 -20, 24 12, 36 -6 s 24 -14, 40 4 s 20 6, 30 -4" stroke="var(--accent)" strokeWidth="2.2" fill="none" strokeLinecap="round" />
    <circle cx="250" cy="190" r="13" fill="#1f9d55" />
    <path d="M244 190 l4 4 8 -9" stroke="#fff" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const TourPay = () => (
  <svg viewBox="0 0 360 260" fill="none" aria-hidden="true">
    <rect x="60" y="20" width="240" height="220" rx="12" fill="var(--panel)" stroke="var(--line)" />
    <text x="80" y="50" fontFamily="IBM Plex Mono" fontSize="9" letterSpacing="1" fill="var(--slate-2)">INVOICE #0421</text>
    <rect x="234" y="38" width="46" height="16" rx="8" fill="color-mix(in srgb,#1f9d55 16%,transparent)" />
    <text x="257" y="49" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill="#1f9d55">PAID</text>
    {[["Foundation", "$5,200", 74], ["Framing", "$4,300", 98], ["Fittings", "$2,900", 122]].map(([a, b, y]) => (
      <g key={a}>
        <text x="80" y={y} fontFamily="IBM Plex Sans" fontSize="11" fill="var(--ink-soft)">{a}</text>
        <text x="280" y={y} textAnchor="end" fontFamily="IBM Plex Mono" fontSize="11" fill="var(--ink)">{b}</text>
        <path d={`M80 ${y + 8} H280`} stroke="var(--line)" />
      </g>
    ))}
    <text x="80" y="160" fontFamily="Archivo" fontWeight="700" fontSize="13" fill="var(--ink)">Total</text>
    <text x="280" y="160" textAnchor="end" fontFamily="Archivo" fontWeight="800" fontSize="15" fill="var(--accent-deep)">$12,400</text>
    <g transform="translate(80 178)">
      <rect x="0" y="34" width="16" height="24" rx="2" fill="var(--line-strong)" />
      <rect x="26" y="22" width="16" height="36" rx="2" fill="var(--line-strong)" />
      <rect x="52" y="10" width="16" height="48" rx="2" fill="var(--accent)" />
      <rect x="78" y="28" width="16" height="30" rx="2" fill="var(--line-strong)" />
      <path d="M8 32 L34 20 L60 8 L86 24" stroke="var(--accent)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  </svg>
);

const TOUR_STEPS = [
  { key: "capture", label: "Site capture", title: "Capture the site in AR", body: "Scan the job site with the app to generate a clean, measured floor plan — ESX site proof tied straight to the project record.", visual: <TourCapture /> },
  { key: "dashboard", label: "Admin dashboard", title: "Run everything from one dashboard", body: "Track every project, estimate, bid and payment in real time, with your whole office working from one source of truth.", visual: <TourDash /> },
  { key: "apps", label: "Client & sub apps", title: "Clients and subcontractors, connected", body: "Clients submit evidence, review invoices, approve and pay. Subcontractors bid, log attendance and upload completion proof.", visual: <TourApps /> },
  { key: "contracts", label: "Estimates & e-sign", title: "Estimates, contracts & e-signatures", body: "Send estimates and proposals, collect electronic signatures, and manage change orders with contract revisions as work evolves.", visual: <TourContract /> },
  { key: "payments", label: "Invoices & payments", title: "Invoices, job-cost & payments", body: "Raise invoices and payment requests, track budgets and job-cost, and keep every number tied to the project.", visual: <TourPay /> },
];

function TourModal({ open, step, onPrev, onNext, onDot, onClose, onBook }) {
  const dialogRef = useRef(null);
  const count = TOUR_STEPS.length;
  const s = TOUR_STEPS[step];
  const isLast = step === count - 1;

  useEffect(() => {
    if (!open) return;
    const dlg = dialogRef.current;
    const focusables = () =>
      dlg ? Array.from(dlg.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])')).filter((el) => !el.disabled && el.offsetParent !== null) : [];
    const first = focusables()[0];
    if (first) first.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      else if (e.key === "ArrowRight") { onNext(); }
      else if (e.key === "ArrowLeft") { onPrev(); }
      else if (e.key === "Tab") {
        const f = focusables();
        if (!f.length) return;
        const idx = f.indexOf(document.activeElement);
        if (e.shiftKey && idx <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && idx === f.length - 1) { e.preventDefault(); f[0].focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, step, onClose, onNext, onPrev]);

  if (!open) return null;
  return (
    <div className="tour-overlay" onClick={onClose}>
      <div className="tour-dialog" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="tourTitle" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="tour-close" onClick={onClose} aria-label="Close product tour"><CloseIc /></button>
        <div className="tour-visual" aria-hidden="true">{s.visual}</div>
        <div className="tour-body">
          <div className="tour-top">
            <span className="eyebrow">Product tour</span>
            <span className="tour-count">{step + 1} / {count}</span>
          </div>
          <div className="tour-label">{s.label}</div>
          <h3 id="tourTitle">{s.title}</h3>
          <p>{s.body}</p>
          <div className="tour-dots" role="tablist" aria-label="Tour steps">
            {TOUR_STEPS.map((st, i) => (
              <button key={st.key} type="button" role="tab" aria-selected={i === step} aria-label={`Step ${i + 1}: ${st.label}`} className={i === step ? "active" : ""} onClick={() => onDot(i)} />
            ))}
          </div>
          <div className="tour-actions">
            <button type="button" className="btn btn-ghost" onClick={onPrev} disabled={step === 0}>Back</button>
            {isLast ? (
              <button type="button" className="btn btn-primary" onClick={onBook}>Book a Demo <ArrowRight /></button>
            ) : (
              <button type="button" className="btn btn-primary" onClick={onNext}>Next <ArrowRight /></button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SubscriptionCheckoutModal({ selection, onClose }) {
  const [values, setValues] = useState({ fullName: "", companyName: "", email: "", phoneNumber: "", acceptTerms: false, acceptPrivacy: false, emailConsent: true });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const requestKey = useRef("");

  useEffect(() => {
    requestKey.current = "";
    setStatus({ type: "", message: "" });
    setSubmitting(false);
    if (!selection) setValues({ fullName: "", companyName: "", email: "", phoneNumber: "", acceptTerms: false, acceptPrivacy: false, emailConsent: true });
  }, [selection]);

  useEffect(() => {
    if (!selection) return undefined;
    const onKey = (event) => { if (event.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selection, submitting, onClose]);

  if (!selection) return null;
  const { plan, price, presentation } = selection;
  const update = (event) => {
    const { name, type, checked, value } = event.target;
    setValues((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };
  const submit = async (event) => {
    event.preventDefault();
    if (!price?.id) {
      setStatus({ type: "error", message: "This plan is not connected to live Stripe checkout yet." });
      return;
    }
    setSubmitting(true);
    setStatus({ type: "", message: "" });
    try {
      if (!requestKey.current) requestKey.current = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `checkout-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const response = await fetch(`${apiBaseUrl}/subscription/self-service/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": requestKey.current },
        body: JSON.stringify({
          ...values,
          priceId: price.id,
          ctaLocation: selection.ctaLocation,
          policyVersion: "2026-08",
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.message || "Unable to start secure checkout.");
      if (!payload.url) throw new Error("Stripe checkout did not return a secure URL.");
      track("self_service_checkout_redirected", { plan: plan.name, billing: price.billingKey, price_id: price.id, authenticated: false });
      window.location.assign(payload.url);
    } catch (error) {
      setStatus({ type: "error", message: error.message || "Unable to start secure checkout." });
      setSubmitting(false);
    }
  };

  return <div className="checkout-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !submitting) onClose(); }}>
    <section className="checkout-dialog" role="dialog" aria-modal="true" aria-labelledby="checkout-title">
      <button className="checkout-close" type="button" aria-label="Close subscription checkout" onClick={onClose} disabled={submitting}><CloseIc /></button>
      <div className="checkout-summary">
        <span className="eyebrow">Self-service onboarding</span>
        <h2 id="checkout-title">Start with {plan.name}</h2>
        <p>{plan.audience}</p>
        <div className="checkout-price"><strong>{formatCurrency(presentation.amount, price.currency)}</strong><span>{presentation.per}</span></div>
        <small>{presentation.billed}{plan.onboarding !== null && plan.onboarding !== undefined ? ` · ${formatCurrency(plan.onboarding, price.currency)} onboarding charged once` : ""}</small>
        <ol><li>Enter your company details</li><li>Pay securely with Stripe</li><li>Activate your Nailed workspace from your email</li></ol>
      </div>
      <form className="checkout-form" onSubmit={submit}>
        <label>Owner's full name<input autoFocus required name="fullName" value={values.fullName} onChange={update} autoComplete="name" /></label>
        <label>Contractor company<input required name="companyName" value={values.companyName} onChange={update} autoComplete="organization" /></label>
        <label>Company email<input required name="email" type="email" value={values.email} onChange={update} autoComplete="email" /></label>
        <label>Phone number <small>Optional</small><input name="phoneNumber" type="tel" value={values.phoneNumber} onChange={update} autoComplete="tel" /></label>
        <label className="checkout-consent"><input required name="acceptTerms" type="checkbox" checked={values.acceptTerms} onChange={update} /><span>I agree to Nailed's Terms of Service.</span></label>
        <label className="checkout-consent"><input required name="acceptPrivacy" type="checkbox" checked={values.acceptPrivacy} onChange={update} /><span>I acknowledge Nailed's Privacy Policy.</span></label>
        <label className="checkout-consent"><input name="emailConsent" type="checkbox" checked={values.emailConsent} onChange={update} /><span>Send me onboarding and product guidance by email.</span></label>
        {status.message && <p className={`checkout-status ${status.type}`} role="alert">{status.message}</p>}
        <button className="btn btn-primary" type="submit" disabled={submitting}>{submitting ? "Opening secure checkout…" : "Continue to secure checkout"} <ArrowRight /></button>
        <p className="checkout-security">Payment details are entered directly in Stripe and are never stored by Nailed.</p>
      </form>
    </section>
  </div>;
}

function App() {
  const rootRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [billing, setBilling] = useState("annual"); // annual is the default
  const [plans, setPlans] = useState(() => hydrateFallbackPlans(PLANS));
  const [lead, setLead] = useState(null); // prefill for the consultation form
  const [checkoutSelection, setCheckoutSelection] = useState(null);
  const [checkoutResult] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("checkout") || ""; }
    catch (e) { return ""; }
  });
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const billingRef = useRef("annual");
  const tourTriggerRef = useRef(null);
  const [isDark, setIsDark] = useState(() => {
    try {
      const s = localStorage.getItem("nailed-theme");
      if (s === "dark" || s === "light") return s === "dark";
    } catch (e) { /* ignore */ }
    return false; // always start in light mode on first visit
  });

  // Default to light on first visit; honour a saved choice thereafter.
  useEffect(() => {
    let saved = null;
    try { saved = localStorage.getItem("nailed-theme"); } catch (e) { /* ignore */ }
    document.documentElement.setAttribute("data-theme", saved === "dark" ? "dark" : "light");
  }, []);

  const toggleTheme = () => {
    setIsDark((prev) => {
      const next = !prev;
      document.documentElement.setAttribute("data-theme", next ? "dark" : "light");
      try { localStorage.setItem("nailed-theme", next ? "dark" : "light"); } catch (e) { /* ignore */ }
      return next;
    });
  };

  const closeMenu = () => setMenuOpen(false);

  useEffect(() => { billingRef.current = billing; }, [billing]);

  useEffect(() => {
    let active = true;
    fetch(`${apiBaseUrl}/subscription/catalog`, { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error("Pricing catalogue unavailable");
        return response.json();
      })
      .then((catalog) => {
        const nextPlans = plansFromCatalog(catalog, PLANS);
        if (!active || nextPlans.length === 0) return;
        setPlans(nextPlans);
        const periods = pricingPeriods(nextPlans);
        setBilling((current) => periods.includes(current) ? current : (periods[0] || current));
      })
      .catch(() => { /* Keep the complete local pricing fallback visible. */ });
    return () => { active = false; };
  }, []);

  const changeBilling = (mode) => {
    if (mode === billing) return;
    setBilling(mode);
    track("pricing_billing_toggle_changed", { billing: mode, cta_location: "pricing_toggle", authenticated: false });
  };

  const goToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // Use self-service checkout when the live catalogue supplies a Stripe
  // Price. Until then, preserve the working consultation journey.
  const selectPlan = (plan, ctaLocation) => {
    const selectedPrice = priceForPlan(plan, billing);
    if (!selectedPrice) return;
    track(`pricing_${plan.ctaEvent}`, { plan: plan.name, billing, cta_location: ctaLocation, authenticated: false });
    if (!selectedPrice.id) {
      track("self_service_checkout_unavailable", { plan: plan.name, billing, cta_location: ctaLocation, authenticated: false });
      bookDemo(ctaLocation, plan);
      return;
    }
    const presentation = pricePresentation(selectedPrice);
    setCheckoutSelection({ plan, price: selectedPrice, presentation, ctaLocation });
    track("self_service_checkout_opened", { plan: plan.name, billing, price_id: selectedPrice.id, authenticated: false });
  };

  const bookDemo = (ctaLocation, plan) => {
    const selectedPrice = plan && typeof plan === "object" ? priceForPlan(plan, billing) : null;
    const presentation = selectedPrice ? pricePresentation(selectedPrice) : null;
    track("pricing_demo_clicked", { plan: plan?.name || plan || null, billing, cta_location: ctaLocation, authenticated: false });
    setLead({
      selectedPlan: plan && typeof plan === "object" ? {
        id: selectedPrice?.id || plan.id, name: plan.name, billing,
        priceMonthly: presentation?.per === "/month" ? presentation.amount : selectedPrice?.amount,
        annualTotal: selectedPrice?.interval === "year" ? selectedPrice.amount : null,
        onboardingFee: plan.onboarding, currency: selectedPrice?.currency || "USD",
        priceId: selectedPrice?.id || undefined, productId: plan.stripeProductId || undefined,
      } : null,
      ctaIntent: "book_demo",
      ctaLocation,
      ctaEvent: "demo_clicked",
    });
    goToContact();
  };

  const onFaqToggle = (q, open) => {
    if (open) track("pricing_faq_opened", { question: q, billing, authenticated: false });
  };

  // Product tour
  useEffect(() => {
    document.body.style.overflow = tourOpen || checkoutSelection ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [tourOpen, checkoutSelection]);

  const openTour = (ctaLocation) => {
    tourTriggerRef.current = typeof document !== "undefined" ? document.activeElement : null;
    setTourStep(0);
    setTourOpen(true);
    track("product_tour_opened", { cta_location: ctaLocation, billing, authenticated: false });
    track("product_tour_step_viewed", { step: 1, key: TOUR_STEPS[0].key, authenticated: false });
  };
  const closeTour = () => {
    setTourOpen(false);
    const t = tourTriggerRef.current;
    if (t && t.focus) setTimeout(() => t.focus(), 0);
  };
  const gotoTour = (i) => {
    setTourStep(i);
    track("product_tour_step_viewed", { step: i + 1, key: TOUR_STEPS[i].key, authenticated: false });
  };
  const tourNext = () => { if (tourStep < TOUR_STEPS.length - 1) gotoTour(tourStep + 1); };
  const tourPrev = () => { if (tourStep > 0) setTourStep(tourStep - 1); };
  const tourBook = () => { setTourOpen(false); bookDemo("product_tour"); };

  // all scroll/animation behaviour, scoped to this page
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const cleanups = [];

    // load sequence
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => root.classList.add("loaded"));
      cleanups.push(() => cancelAnimationFrame(raf2));
    });
    cleanups.push(() => cancelAnimationFrame(raf1));

    // give drawn SVG paths their real length
    root.querySelectorAll(".plan-svg .draw, .diagram .wire").forEach((p) => {
      try { p.style.setProperty("--len", String(p.getTotalLength())); } catch (e) { /* ignore */ }
    });

    // reveal on scroll
    if (typeof IntersectionObserver !== "undefined" && !reduce) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          en.target.classList.add("in");
          en.target.querySelectorAll(".bar i").forEach((b) => { b.style.width = (b.dataset.w || "0") + "%"; });
          io.unobserve(en.target);
        });
      }, { threshold: 0.18 });
      root.querySelectorAll(".reveal, .diagram").forEach((el) => io.observe(el));
      cleanups.push(() => io.disconnect());

      // counters
      const cio = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          const el = en.target;
          const target = parseFloat(el.dataset.count);
          const dec = el.dataset.decimal ? 1 : 0;
          const dur = 1300;
          let t0 = null;
          const step = (ts) => {
            if (t0 === null) t0 = ts;
            const prog = Math.min((ts - t0) / dur, 1);
            const eased = 1 - Math.pow(1 - prog, 3);
            el.textContent = (target * eased).toFixed(dec);
            if (prog < 1) requestAnimationFrame(step);
            else el.textContent = target.toFixed(dec);
          };
          requestAnimationFrame(step);
          cio.unobserve(el);
        });
      }, { threshold: 0.4 });
      root.querySelectorAll("[data-count]").forEach((el) => cio.observe(el));
      cleanups.push(() => cio.disconnect());
    } else {
      root.querySelectorAll(".reveal, .diagram").forEach((el) => el.classList.add("in"));
      root.querySelectorAll(".bar i").forEach((b) => { b.style.width = (b.dataset.w || "0") + "%"; });
      root.querySelectorAll("[data-count]").forEach((el) => {
        el.textContent = parseFloat(el.dataset.count).toFixed(el.dataset.decimal ? 1 : 0);
      });
    }

    // nav shadow on scroll
    const nav = root.querySelector(".site-nav");
    const onScroll = () => { if (nav) nav.classList.toggle("scrolled", window.scrollY > 12); };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    cleanups.push(() => window.removeEventListener("scroll", onScroll));

    // hero parallax
    const sheet = root.querySelector(".stage .sheet");
    if (sheet && !reduce) {
      const onMove = (e) => {
        const x = e.clientX / window.innerWidth - 0.5;
        const y = e.clientY / window.innerHeight - 0.5;
        sheet.style.transform = `translate(${x * 10}px, ${y * 8}px)`;
      };
      window.addEventListener("mousemove", onMove, { passive: true });
      cleanups.push(() => window.removeEventListener("mousemove", onMove));
    }

    // AR canvas
    const cv = root.querySelector("#arCanvas");
    let animId = null;
    if (cv && cv.getContext) {
      const ctx = cv.getContext("2d");
      const W = cv.width, H = cv.height;
      const accent = getComputedStyle(document.documentElement).getPropertyValue("--accent").trim() || "#ea5220";
      const seg = [[0.2, 0.2, 0.8, 0.2], [0.8, 0.2, 0.8, 0.8], [0.8, 0.8, 0.2, 0.8], [0.2, 0.8, 0.2, 0.2], [0.5, 0.2, 0.5, 0.55], [0.2, 0.55, 0.5, 0.55], [0.5, 0.4, 0.8, 0.4]];
      const pts = [];
      for (let i = 0; i < 70; i++) pts.push({ x: Math.random(), y: Math.random(), s: Math.random() * 1.6 + 0.4, ph: Math.random() * 6.28 });
      let t = 0;
      const frame = () => {
        t += reduce ? 0 : 0.012;
        ctx.clearRect(0, 0, W, H);
        const cyc = Math.sin(t) * 0.5 + 0.5;
        ctx.fillStyle = "rgba(255,255,255,.5)";
        pts.forEach((p) => {
          const tw = Math.sin(t * 2 + p.ph) * 0.5 + 0.5;
          ctx.globalAlpha = 0.15 + tw * 0.5 * (1 - cyc * 0.7);
          ctx.beginPath();
          ctx.arc((p.x * 0.7 + 0.15) * W, (p.y * 0.7 + 0.15) * H, p.s, 0, 6.28);
          ctx.fill();
        });
        ctx.globalAlpha = 1;
        ctx.strokeStyle = "rgba(255,255,255," + (0.25 + cyc * 0.6) + ")";
        ctx.lineWidth = 1.6;
        seg.forEach((s, idx) => {
          const prog = Math.max(0, Math.min(1, cyc * seg.length - idx));
          if (prog <= 0) return;
          ctx.beginPath();
          ctx.moveTo(s[0] * W, s[1] * H);
          ctx.lineTo(s[0] * W + (s[2] - s[0]) * W * prog, s[1] * H + (s[3] - s[1]) * H * prog);
          ctx.stroke();
        });
        const sy = (t * 0.15) % 1;
        const grad = ctx.createLinearGradient(0, sy * H - 30, 0, sy * H + 30);
        grad.addColorStop(0, "rgba(234,82,32,0)");
        grad.addColorStop(0.5, accent);
        grad.addColorStop(1, "rgba(234,82,32,0)");
        ctx.strokeStyle = grad; ctx.lineWidth = 2; ctx.globalAlpha = 0.7;
        ctx.beginPath(); ctx.moveTo(0.12 * W, sy * H); ctx.lineTo(0.88 * W, sy * H); ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = accent; ctx.lineWidth = 2;
        [[0.15, 0.15], [0.85, 0.15], [0.85, 0.85], [0.15, 0.85]].forEach((c, i) => {
          const dx = i === 0 || i === 3 ? 1 : -1;
          const dy = i < 2 ? 1 : -1;
          ctx.beginPath();
          ctx.moveTo(c[0] * W, c[1] * H); ctx.lineTo(c[0] * W + dx * 14, c[1] * H);
          ctx.moveTo(c[0] * W, c[1] * H); ctx.lineTo(c[0] * W, c[1] * H + dy * 14);
          ctx.stroke();
        });
        animId = requestAnimationFrame(frame);
      };
      frame();
      cleanups.push(() => { if (animId) cancelAnimationFrame(animId); });
    }

    // pricing analytics — fire once as each section enters view
    if (typeof IntersectionObserver !== "undefined") {
      const seen = {};
      const aio = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          const id = en.target.id;
          if (id === "pricing" && !seen.pricing) { seen.pricing = true; track("pricing_page_viewed", { billing: billingRef.current, authenticated: false }); }
          if (id === "pricing-compare" && !seen.compare) { seen.compare = true; track("pricing_comparison_viewed", { billing: billingRef.current, authenticated: false }); }
          aio.unobserve(en.target);
        });
      }, { threshold: 0.25 });
      ["#pricing", "#pricing-compare"].forEach((s) => { const el = root.querySelector(s); if (el) aio.observe(el); });
      cleanups.push(() => aio.disconnect());
    }

    return () => cleanups.forEach((fn) => fn());
  }, []);

  const availableBillingPeriods = pricingPeriods(plans);

  return (
    <main className="nlx nlx-home" ref={rootRef}>
      {/* ---------------- NAV ---------------- */}
      <header className="site-nav">
        <div className="wrap nav-in">
          <a className="brand" href="#hero" aria-label="Nailed home">
            <NailedLogo className="brand-mark" nailColor="var(--accent)" />
          </a>
          <nav className="nav-links">
            {NAV_LINKS.map((l) =>
              l.tour ? (
                <button key="tour" type="button" onClick={() => openTour("nav")}>{l.label}</button>
              ) : (
                <a key={l.href} href={l.href}>{l.label}</a>
              )
            )}
          </nav>
          <div className="nav-right">
            <button className="themebtn" onClick={toggleTheme} aria-label="Toggle color theme" title="Toggle theme">
              {isDark ? <Sun /> : <Moon />}
            </button>
            <a href="#contact" className="btn btn-primary nav-book">
              Book Demo <ArrowRight />
            </a>
            <button className="menubtn" onClick={() => setMenuOpen(true)} aria-label="Open menu">
              <MenuIc />
            </button>
          </div>
        </div>
      </header>

      {/* mobile drawer */}
      <div className={`scrim${menuOpen ? " open" : ""}`} onClick={closeMenu} aria-hidden="true" />
      <aside className={`drawer${menuOpen ? " open" : ""}`}>
        <button className="menubtn" style={{ display: "grid", marginLeft: "auto" }} onClick={closeMenu} aria-label="Close menu">
          <CloseIc />
        </button>
        {NAV_LINKS.map((l) =>
          l.tour ? (
            <button key="tour" type="button" onClick={() => { closeMenu(); openTour("nav"); }}>{l.label}</button>
          ) : (
            <a key={l.href} href={l.href} onClick={closeMenu}>{l.label}</a>
          )
        )}
        <a href="#contact" className="btn btn-primary" onClick={closeMenu}>
          Book Demo <ArrowRight />
        </a>
      </aside>

      {/* ---------------- HERO ---------------- */}
      <section id="hero" className="hero">
        <div className="wrap hero-grid">
          <div className="hero-copy">
            <div className="eyebrow hero-fade hero-eyebrow">Construction Operating System</div>
            <h1>
              <span className="line"><span>Build trust.</span></span>
              <span className="line"><span>Manage every project</span></span>
              <span className="line"><span className="accent">from one app.</span></span>
            </h1>
            <p className="hero-sub hero-fade">
              Bring estimating, project evidence, field updates, claims, invoices, clients and
              subcontractors into one branded platform your customers can trust.
            </p>
            <div className="hero-cta hero-fade">
              <a href="#contact" className="btn btn-primary">Book a free demo <ArrowRight /></a>
              <button type="button" className="btn btn-ghost" onClick={() => openTour("hero")}>Watch product tour</button>
            </div>
            <div className="hero-strip hero-fade">
              <div className="lbl">Trusted construction workflow for modern contractors</div>
              <div className="tags">
                {["Admin Dashboard", "Client App", "Subcontractor App", "AR Site Proof"].map((t) => (
                  <span className="tag" key={t}><b />{t}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="stage hero-fade" style={{ transitionDelay: "0.3s" }}>
            <div className="sheet float">
              <div className="sheet-head">
                <span><span className="dot" />ESX &middot; Site capture</span>
                <span>SHEET&nbsp;A-01</span>
              </div>
              <svg className="plan-svg" viewBox="0 0 400 300" fill="none" aria-label="Floor plan being scanned into the platform">
                <g stroke="var(--line-strong)" strokeWidth={1}>
                  <path className="draw" d="M20 22 H380" />
                  <path className="draw" d="M20 18 V26 M380 18 V26 M200 18 V26" />
                </g>
                <text x="108" y="16" fill="var(--slate-2)" fontFamily="IBM Plex Mono" fontSize="9">24' 0"</text>
                <text x="290" y="16" fill="var(--slate-2)" fontFamily="IBM Plex Mono" fontSize="9">24' 0"</text>
                <rect className="draw" x="20" y="40" width="360" height="240" rx="2" stroke="var(--ink)" strokeWidth={2.4} />
                <g stroke="var(--ink-soft)" strokeWidth={1.8}>
                  <path className="draw" d="M200 40 V180" />
                  <path className="draw" d="M20 180 H200" />
                  <path className="draw" d="M200 120 H380" />
                  <path className="draw" d="M290 120 V280" />
                </g>
                <g stroke="var(--accent)" strokeWidth={1.6}>
                  <path className="draw" d="M120 180 A30 30 0 0 1 150 210" />
                  <path className="draw" d="M200 90 A26 26 0 0 0 226 116" />
                </g>
                <g stroke="var(--slate-2)" strokeWidth={1.3}>
                  <rect className="draw" x="40" y="60" width="42" height="26" rx="2" />
                  <circle className="draw" cx="330" cy="80" r="16" />
                  <rect className="draw" x="230" y="210" width="40" height="50" rx="2" />
                </g>
                <text x="70" y="130" fill="var(--slate-2)" fontFamily="IBM Plex Mono" fontSize="8.5">LIVING</text>
                <text x="250" y="86" fill="var(--slate-2)" fontFamily="IBM Plex Mono" fontSize="8.5">KITCHEN</text>
                <text x="70" y="238" fill="var(--slate-2)" fontFamily="IBM Plex Mono" fontSize="8.5">BED 01</text>
                <text x="315" y="210" fill="var(--slate-2)" fontFamily="IBM Plex Mono" fontSize="8.5">BATH</text>
                <g className="scanline">
                  <rect x="20" y="40" width="360" height="3" fill="var(--accent)" />
                  <rect x="20" y="43" width="360" height="26" fill="url(#nlG1)" opacity="0.5" />
                </g>
                <defs>
                  <linearGradient id="nlG1" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0" stopColor="var(--accent)" stopOpacity="0.5" />
                    <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            <div className="hero-mini float d1">
              <div className="hm-h"><b />ACTIVE JOBS</div>
              <div className="hm-n">18</div>
              <svg className="hm-spark" viewBox="0 0 120 34" preserveAspectRatio="none" aria-hidden="true">
                <defs>
                  <linearGradient id="nlSpark" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0" stopColor="var(--accent)" stopOpacity="0.28" />
                    <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path className="area" d="M0 26 L18 22 L36 24 L54 16 L72 18 L90 9 L108 12 L120 6 L120 34 L0 34 Z" />
                <path className="ln" d="M0 26 L18 22 L36 24 L54 16 L72 18 L90 9 L108 12 L120 6" />
                <circle className="dot" cx="120" cy="6" r="3" />
              </svg>
              <div className="hm-f">+3 this week</div>
            </div>

            <div className="hero-phone float d2">
              <svg viewBox="0 0 170 340" fill="none" aria-hidden="true">
                <defs>
                  <linearGradient id="nlPB" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0" stopColor="var(--accent)" stopOpacity="0.45" />
                    <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <rect x="6" y="4" width="158" height="332" rx="28" fill="var(--block)" />
                <rect x="15" y="14" width="140" height="312" rx="20" fill="var(--paper-2)" />
                <rect x="72" y="22" width="26" height="5" rx="2.5" fill="var(--block)" />
                <text x="30" y="52" fontFamily="IBM Plex Mono" fontSize="9" letterSpacing="1.5" fill="var(--slate-2)">ESX · SCAN</text>
                <g stroke="var(--ink-soft)" strokeWidth="1.6">
                  <rect x="30" y="70" width="110" height="150" rx="2" stroke="var(--ink)" strokeWidth="2" />
                  <path d="M85 70 V150" />
                  <path d="M30 150 H85" />
                  <path d="M85 130 H140" />
                </g>
                <path d="M55 150 A16 16 0 0 1 71 166" stroke="var(--accent)" strokeWidth="1.6" />
                <text x="44" y="112" fontFamily="IBM Plex Mono" fontSize="7" fill="var(--slate-2)">LIVING</text>
                <text x="100" y="106" fontFamily="IBM Plex Mono" fontSize="7" fill="var(--slate-2)">KIT</text>
                <g className="pbeam">
                  <rect x="30" y="70" width="110" height="3" fill="var(--accent)" />
                  <rect x="30" y="73" width="110" height="20" fill="url(#nlPB)" />
                </g>
                <g stroke="var(--accent)" strokeWidth="2" fill="none">
                  <path d="M30 86 V78 H38" />
                  <path d="M140 86 V78 H132" />
                </g>
                <rect x="30" y="242" width="110" height="34" rx="10" fill="var(--accent)" />
                <text x="85" y="263" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8.5" fill="#fff" letterSpacing="1">GENERATE PLAN</text>
                <circle cx="85" cy="302" r="11" fill="none" stroke="var(--line-strong)" strokeWidth="2" />
              </svg>
            </div>

            <div className="chip c1 float d1">
              <span className="ic"><Check /></span>
              <div>Invoice paid<small>Client app</small></div>
            </div>
            <div className="chip c2 float d2">
              <span className="ic"><Layers /></span>
              <div>New bid received<small>Subcontractor</small></div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- PILLARS ---------------- */}
      <section id="platform" className="sec" style={{ paddingTop: 60 }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <div className="eyebrow">01 &middot; The platform</div>
            <h2>The operating system behind modern builders</h2>
            <p>Nailed connects sales, admin operations, clients, subcontractors, site evidence, claims and payments in one premium workflow.</p>
          </div>
          <div className="pillars">
            <article className="pcard reveal d1">
              <div className="pill-art"><IntakeArt /><span className="pnum">A-01</span></div>
              <h3>Project Intake</h3>
              <p>Capture client requests, files, media, plans and scope details before work begins — nothing lost in phone calls or scattered chats.</p>
              <a href="#contact" className="more">Learn more <ArrowRight /></a>
            </article>
            <article className="pcard reveal d2">
              <div className="pill-art"><FieldArt /><span className="pnum">A-02</span></div>
              <h3>Field Operations</h3>
              <p>Give teams job-site tools for attendance, updates, proof and completion flows — a real operating rhythm from first visit to sign-off.</p>
              <a href="#contact" className="more">Learn more <ArrowRight /></a>
            </article>
            <article className="pcard reveal d3">
              <div className="pill-art"><FinanceArt /><span className="pnum">A-03</span></div>
              <h3>Finance &amp; Claims</h3>
              <p>Keep estimates, invoices, payouts, receipts and claims tied to each project — every number traceable to the work it belongs to.</p>
              <a href="#contact" className="more">Learn more <ArrowRight /></a>
            </article>
          </div>
        </div>
      </section>

      {/* ---------------- THESIS ---------------- */}
      <section className="sec" style={{ paddingTop: 20 }}>
        <div className="wrap reveal">
          <div className="thesis">
            <div className="in">
              <div className="eyebrow">The point</div>
              <h2 style={{ marginTop: 22 }}>We do not just manage projects. <span className="u">We build trust.</span></h2>
              <p>Nailed was built for construction companies that need every quote, site visit, client conversation, subcontractor bid, proof upload, claim and payment to feel organized from day one. Residential builds, renovations, commercial work, or specialist subcontracting — one clear place to operate.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- USE CASES ---------------- */}
      <section id="use-cases" className="sec" style={{ paddingTop: 40 }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <div className="eyebrow">02 &middot; Use cases</div>
            <h2>Built for every kind of construction business</h2>
            <p>Residential, renovation, commercial or specialist — Nailed adapts to how your team already works, from the first quote to final payment.</p>
          </div>
          <div className="ucards">
            {USE_CASES.map((u, i) => (
              <article className={`ucard reveal d${(i % 3) + 1}`} key={u.title}>
                <div className="uicon">{u.icon}</div>
                <h3>{u.title}</h3>
                <p>{u.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- PLATFORM DIAGRAM ---------------- */}
      <section className="sec" style={{ paddingTop: 40 }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <div className="eyebrow">03 &middot; One connected process</div>
            <h2>Everyone works from the same source of truth</h2>
            <p>There is no reason for construction companies to look disorganized when the work is complex. Nailed ties every role to one shared record.</p>
          </div>
          <div className="os">
            <div className="os-list reveal">
              <div className="os-item"><div className="k">S1</div><div><h4>Sales &amp; estimating</h4><p>Quotes and scope built and sent from the same record.</p></div></div>
              <div className="os-item"><div className="k">S2</div><div><h4>Admin dashboard</h4><p>Full visibility across projects, people, finance and media.</p></div></div>
              <div className="os-item"><div className="k">S3</div><div><h4>Clients</h4><p>Submit evidence, review invoices, pay and track updates.</p></div></div>
              <div className="os-item"><div className="k">S4</div><div><h4>Subcontractors</h4><p>Bid, manage attendance, upload proof and receive payouts.</p></div></div>
              <div className="os-item"><div className="k">S5</div><div><h4>Site evidence &amp; payments</h4><p>Claims, receipts and payments tied to every project.</p></div></div>
            </div>
            <div className="diagram">
              <svg viewBox="0 0 440 340" aria-label="Six roles — sales, admin, clients, subcontractors, payments and site proof — connecting into one central Nailed record">
                {/* connecting wires (node edge -> hub) */}
                <path className="wire" d="M104 51 C 165 55, 182 100, 198 124" />
                <path className="wire" d="M336 51 C 275 55, 258 100, 242 124" />
                <path className="wire" d="M104 161 C 140 163, 156 167, 172 168" />
                <path className="wire" d="M336 161 C 300 163, 284 167, 268 168" />
                <path className="wire" d="M104 271 C 165 267, 182 240, 198 216" />
                <path className="wire" d="M336 271 C 275 267, 258 240, 242 216" />
                {/* animated flow along a diagonal set */}
                <path className="flow" d="M104 51 C 165 55, 182 100, 198 124" />
                <path className="flow" d="M336 161 C 300 163, 284 167, 268 168" />
                <path className="flow" d="M336 271 C 275 267, 258 240, 242 216" />
                {/* role nodes */}
                <g className="nodeboxg" fontFamily="IBM Plex Mono" fontSize="10">
                  <g transform="translate(16,36)"><rect width="88" height="30" rx="7" fill="var(--paper)" stroke="var(--line-strong)" /><text x="44" y="19" textAnchor="middle" fill="var(--ink)">Sales</text></g>
                  <g transform="translate(16,146)"><rect width="88" height="30" rx="7" fill="var(--paper)" stroke="var(--line-strong)" /><text x="44" y="19" textAnchor="middle" fill="var(--ink)">Clients</text></g>
                  <g transform="translate(16,256)"><rect width="88" height="30" rx="7" fill="var(--paper)" stroke="var(--line-strong)" /><text x="44" y="19" textAnchor="middle" fill="var(--ink)">Subs</text></g>
                  <g transform="translate(336,36)"><rect width="88" height="30" rx="7" fill="var(--paper)" stroke="var(--line-strong)" /><text x="44" y="19" textAnchor="middle" fill="var(--ink)">Admin</text></g>
                  <g transform="translate(336,146)"><rect width="88" height="30" rx="7" fill="var(--paper)" stroke="var(--line-strong)" /><text x="44" y="19" textAnchor="middle" fill="var(--ink)">Payments</text></g>
                  <g transform="translate(336,256)"><rect width="88" height="30" rx="7" fill="var(--paper)" stroke="var(--line-strong)" /><text x="44" y="19" textAnchor="middle" fill="var(--ink)">Site proof</text></g>
                </g>
                {/* central hub (drawn last so wire ends tuck under it) */}
                <g className="nodeboxg">
                  <circle cx="220" cy="170" r="68" fill="none" stroke="var(--accent)" strokeWidth={1} strokeDasharray="3 6" opacity="0.45" />
                  <circle cx="220" cy="170" r="50" fill="var(--accent-tint)" stroke="var(--accent)" strokeWidth={1.5} />
                  <text x="220" y="166" textAnchor="middle" fontFamily="Archivo" fontWeight="800" fontSize="17" fill="var(--accent-deep)" letterSpacing="-.5">NAILED</text>
                  <text x="220" y="182" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8" fill="var(--accent-deep)" letterSpacing="1.5">ONE RECORD</text>
                </g>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- STATS ---------------- */}
      <section className="sec" style={{ paddingTop: 20 }}>
        <div className="wrap reveal">
          <div className="stats">
            <div className="stat"><span className="k">/01</span><div className="n" data-count="1">0</div><div className="l">Platform for the whole company</div></div>
            <div className="stat"><span className="k">/02</span><div className="n" data-count="3">0</div><div className="l">Connected apps: admin, client &amp; subcontractor</div></div>
            <div className="stat"><span className="k">/03</span><div className="n" data-count="6">0</div><div className="l">Workflows unified in one flow, not five tools</div></div>
            <div className="stat"><span className="k">/04</span><div className="n"><span data-count="5" data-decimal="1">0</span><span className="s">&#9733;</span></div><div className="l">Rating from early teams in UT &middot; TX &middot; AZ</div></div>
          </div>
        </div>
      </section>

      {/* ---------------- APPS ---------------- */}
      <section id="mobile" className="sec" style={{ paddingTop: 60 }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <div className="eyebrow">04 &middot; Mobile apps</div>
            <h2>Apps for clients and subcontractors — and one dashboard for you</h2>
            <p>Sell confidence before the first site visit. Every inbox item, bid, payout and proof upload lives in one place.</p>
          </div>

          <div className="approw reveal">
            <div className="txt">
              <div className="eyebrow muted">Admin dashboard</div>
              <h3>Total visibility across projects, people and finance</h3>
              <div className="featli">
                <div><span className="ck"><Check /></span>Track every project, quote and payment in real time</div>
                <div><span className="ck"><Check /></span>Estimates, invoices, claims and field updates in one workflow</div>
                <div><span className="ck"><Check /></span>One reliable source of truth for the whole office</div>
              </div>
            </div>
            <div className="app-visual">
              <div className="device">
                <div className="dev-bar"><i /><i /><i /><span>nailed.app / dashboard</span></div>
                <div className="dev-body">
                  <div className="kpis">
                    <div className="kpi"><div className="n" data-count="18">0</div><div className="l">Active jobs</div></div>
                    <div className="kpi"><div className="n">$<span data-count="240">0</span>k</div><div className="l">Invoiced</div></div>
                    <div className="kpi"><div className="n" data-count="7">0</div><div className="l">Open bids</div></div>
                  </div>
                  <div className="rows">
                    <div className="rw"><span className="av">LV</span><div className="meta"><b>Lakeview Renovation</b><small>Field ops · proof uploaded</small><div className="bar"><i data-w="82" /></div></div><span className="pill ok">ON&nbsp;TRACK</span></div>
                    <div className="rw"><span className="av">MC</span><div className="meta"><b>Maple Commercial Fit-out</b><small>Awaiting client approval</small><div className="bar"><i data-w="46" /></div></div><span className="pill pend">REVIEW</span></div>
                    <div className="rw"><span className="av">RH</span><div className="meta"><b>Ridge House Build</b><small>Invoice #0421 · paid</small><div className="bar"><i data-w="100" /></div></div><span className="pill ok">PAID</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="approw rev reveal">
            <div className="txt">
              <div className="eyebrow muted">Client &amp; subcontractor apps</div>
              <h3>Clients submit &amp; pay. Subcontractors bid &amp; get paid.</h3>
              <div className="featli">
                <div><span className="ck"><Check /></span>Clients submit project evidence, review invoices, pay and track updates</div>
                <div><span className="ck"><Check /></span>Subcontractors bid, manage job-site attendance and upload completion proof</div>
                <div><span className="ck"><Check /></span>Bids, payouts and inboxes kept in one place</div>
              </div>
              <div className="app-tags">
                {["Bid market", "Job-site map", "Payouts", "Claims"].map((t) => (
                  <span className="tag" key={t}><b />{t}</span>
                ))}
              </div>
            </div>
            <div className="app-visual">
              <div className="phone">
                <div className="phone-screen">
                  <div className="ph-top">
                    <div className="e">Client · Ridge House</div>
                    <h5>Your project updates</h5>
                  </div>
                  <div className="ph-body">
                    <div className="ph-card">
                      <div className="t">Foundation complete <small>MON</small></div>
                      <div className="ph-thumb" />
                    </div>
                    <div className="ph-card">
                      <div className="t">Invoice #0421 <span className="pill ok" style={{ fontSize: 9 }}>PAID</span></div>
                      <small>$12,400 · paid via card</small>
                    </div>
                    <div className="ph-card">
                      <div className="t">Next: framing inspection</div>
                      <small>Scheduled Thu · you'll get a proof upload</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- AR SPOTLIGHT ---------------- */}
      <section id="proof" className="sec" style={{ paddingTop: 20 }}>
        <div className="wrap reveal">
          <div className="ar">
            <div className="ar-grid">
              <div className="ar-txt">
                <div className="eyebrow">AR site proof &middot; ESX</div>
                <h2>Turn a site scan into an organized floor plan</h2>
                <p>Capture the job site in AR, drop notes on video, and generate a clean floor plan — proof your clients and crews can trust, tied straight to the project record.</p>
                <div className="ar-steps">
                  <div className="ar-step"><div className="n">01</div><div><h4>Scan the space</h4><p>Walk the site with the app; capture depth and dimensions.</p></div></div>
                  <div className="ar-step"><div className="n">02</div><div><h4>Annotate in AR</h4><p>Pin notes to video and mark issues where they happen.</p></div></div>
                  <div className="ar-step"><div className="n">03</div><div><h4>Generate the plan</h4><p>A measured floor plan attaches to the project automatically.</p></div></div>
                </div>
              </div>
              <div className="ar-canvaswrap">
                <canvas id="arCanvas" width="520" height="520" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- PRICING ---------------- */}
      <section id="pricing" className="sec" style={{ paddingTop: 60 }}>
        <div className="wrap">
          {checkoutResult === "success" && <div className="checkout-result success" role="status"><strong>Subscription confirmed.</strong> Check your company email for the secure workspace activation link.</div>}
          {checkoutResult === "cancelled" && <div className="checkout-result cancelled" role="status"><strong>Checkout was cancelled.</strong> Your plan remains available whenever you are ready.</div>}
          <div className="sec-head reveal" style={{ maxWidth: 780 }}>
            <div className="eyebrow">Plans &amp; pricing</div>
            <h2>Plans built to get contractors paid faster</h2>
            <p>Keep projects, site proof, client approvals, change orders, invoices, and payments moving from one place.</p>
            <div className="trust-line">No charge for clients or subcontractors. Upgrade as your company grows.</div>
          </div>

          <div className="billing-toggle-wrap reveal">
            <div className="billing-toggle" role="group" aria-label="Billing period">
              {availableBillingPeriods.map((period) => {
                const price = plans.find((plan) => priceForPlan(plan, period))?.priceOptions?.[period];
                return (
                  <button type="button" key={period} className={billing === period ? "active" : ""} aria-pressed={billing === period} onClick={() => changeBilling(period)}>
                    {price ? billingLabel(price) : period}
                    {period === "annual" && <span className="save">Save 20%</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="price-grid">
            {plans.map((plan, i) => {
              const price = priceForPlan(plan, billing);
              const presentation = pricePresentation(price);
              return <article className={`price-card reveal d${i + 1}${plan.popular ? " popular" : ""}`} key={plan.id}>
                {plan.popular && <div className="pop-badge">Most Popular</div>}
                <h3>{plan.name}</h3>
                <p className="plan-aud">{plan.audience}</p>

                <div className="plan-price">
                  {price && presentation ? <>
                    <div className="amount-row">
                      {plan.startingAt && <span className="starting">Starting at</span>}
                      <span className="amount">{fmt(presentation.amount, price.currency)}</span>
                      <span className="per">{presentation.per}</span>
                    </div>
                    <div className="price-sub">{presentation.billed}</div>
                    {plan.onboarding !== null && plan.onboarding !== undefined ? <div className="onboarding-fee">+ {fmt(plan.onboarding, price.currency)} one-time onboarding</div> : <div className="onboarding-fee">Onboarding terms confirmed with sales</div>}
                  </> : (
                    <>
                      <div className="amount-row"><span className="amount">—</span></div>
                      <div className="price-sub">Not offered for this billing period</div>
                    </>
                  )}
                </div>

                <button
                  type="button"
                  className={`btn ${plan.popular ? "btn-primary" : "btn-ghost"} plan-cta`}
                  onClick={() => selectPlan(plan, "pricing_card")}
                  disabled={!price}
                >
                  {plan.ctaLabel} <ArrowRight />
                </button>

                <ul className="plan-limits">
                  {plan.limits.map((l) => (<li key={l}>{l}</li>))}
                </ul>
                <div className="plan-feat-h">Includes</div>
                <ul className="plan-features">
                  {plan.features.map((f) => (
                    <li key={f}><span className="fk"><Check /></span>{f}</li>
                  ))}
                </ul>
              </article>;
            })}
          </div>
        </div>
      </section>

      {/* ---------------- COMPARISON ---------------- */}
      <section id="pricing-compare" className="sec" style={{ paddingTop: 20 }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <div className="eyebrow">Compare plans</div>
            <h2>Everything in each plan, side by side</h2>
          </div>
          <div className="compare-wrap reveal" role="region" aria-label="Plan feature comparison" tabIndex={0}>
            <table className="compare">
              <caption className="sr-only">Feature comparison across the currently offered Nailed plans</caption>
              <thead>
                <tr>
                  <th scope="col">Feature</th>
                  {plans.map((plan) => <th scope="col" key={plan.id} className={plan.popular ? "hl" : ""}>{plan.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((row) => (
                  <tr key={row.label}>
                    <th scope="row">{row.label}</th>
                    {plans.map((plan) => {
                      const v = row.values[plan.comparisonIndex];
                      return <td key={plan.id} className={plan.popular ? "hl" : ""}>
                        {v === true ? (
                          <span className="ck-yes" aria-label="Included"><Check /></span>
                        ) : v === false ? (
                          <span className="ck-no" aria-label="Not included">—</span>
                        ) : (
                          <span className="ck-val">{v}</span>
                        )}
                      </td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ---------------- ONBOARDING ---------------- */}
      <section className="sec" style={{ paddingTop: 20 }}>
        <div className="wrap reveal">
          <div className="onboard-card">
            <div className="onboard-copy">
              <div className="eyebrow">Onboarding</div>
              <h2>Start with your real projects—not an empty account</h2>
              <p>Every Nailed plan includes guided setup to help configure your company, team, project workflow, templates, and client experience.</p>
            </div>
            <div className="onboard-fees">
              {plans.map((p) => {
                const price = priceForPlan(p, billing) || Object.values(p.priceOptions || {})[0];
                return <div className="fee-row" key={p.id}>
                  <span>{p.name}</span>
                  <b>{p.onboarding === null || p.onboarding === undefined ? "Confirmed with sales" : p.onboarding === 0 ? "Included" : `${fmt(p.onboarding, price?.currency || "USD")} one-time`}</b>
                </div>;
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- FAQ ---------------- */}
      <section className="sec" style={{ paddingTop: 20 }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <div className="eyebrow">FAQ</div>
            <h2>Pricing questions, answered</h2>
          </div>
          <div className="faq-list reveal">
            {FAQS.map((f, i) => (
              <details className="faq" key={i} onToggle={(e) => onFaqToggle(f.q, e.currentTarget.open)}>
                <summary>
                  <span>{f.q}</span>
                  <span className="faq-ic" aria-hidden="true"></span>
                </summary>
                <div className="faq-a">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- PRICING CTA ---------------- */}
      <section className="sec" style={{ paddingTop: 20 }}>
        <div className="wrap reveal">
          <div className="price-cta">
            <h2>Ready to stop chasing project information and payments?</h2>
            <p>See how Nailed can move your projects from site work to client approval and payment.</p>
            <div className="price-cta-actions">
              <button type="button" className="btn btn-primary" onClick={() => bookDemo("pricing_final_cta")}>Book a Demo <ArrowRight /></button>
              <button type="button" className="btn btn-ghost" onClick={() => openTour("pricing_final_cta")}>View Product Tour</button>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- TESTIMONIALS ---------------- */}
      <section className="sec" style={{ paddingTop: 60 }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <div className="eyebrow">05 &middot; Customers</div>
            <h2>The teams building with Nailed</h2>
          </div>
          <div className="tgrid">
            <figure className="tcard reveal d1">
              <div className="stars">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
              <blockquote>&ldquo;Projects no longer disappear into phone calls and scattered chats. Our clients see progress faster, and the office finally has one reliable source of truth.&rdquo;</blockquote>
              <figcaption className="who"><span className="av">AB</span><div><b>Amanda Brooks</b><small>Operations Manager · Utah</small></div></figcaption>
            </figure>
            <figure className="tcard reveal d2">
              <div className="stars">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
              <blockquote>&ldquo;Our estimates, invoices, claims and field updates now move through one workflow instead of five disconnected tools.&rdquo;</blockquote>
              <figcaption className="who"><span className="av">MR</span><div><b>Marcus Reed</b><small>Project Director · Texas</small></div></figcaption>
            </figure>
            <figure className="tcard reveal d3">
              <div className="stars">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
              <blockquote>&ldquo;The subcontractor flow gives our crews a real operating rhythm — from bids to site proof to payment tracking.&rdquo;</blockquote>
              <figcaption className="who"><span className="av">SM</span><div><b>Sofia Martinez</b><small>Construction Controller · Arizona</small></div></figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* ---------------- FOUNDER ---------------- */}
      <section className="sec" style={{ paddingTop: 20 }}>
        <div className="wrap reveal">
          <div className="founder">
            <div>
              <div className="badge"><img src="/images/blackman.png" alt="Nailed founder" /></div>
            </div>
            <div>
              <div className="eyebrow muted">Message from the founder</div>
              <h3>Let us help you shape your construction business.</h3>
              <p>With one connected admin dashboard and mobile apps for clients and subcontractors, your company can sell confidence before the first site visit. There is no reason to look disorganized when the work is this complex.</p>
              <div className="sig">— The Nailed team · Utah</div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- CONTACT ---------------- */}
      <section id="contact" className="sec" style={{ paddingTop: 40 }}>
        <div className="wrap cta">
          <div className="reveal">
            <div className="eyebrow">06 &middot; Get started</div>
            <h2>Book a free construction demo</h2>
            <p>Premium construction software for companies that want their admin, clients and site teams in one connected process. Let us discuss your workflow.</p>
            <div className="contactline">
              <a href="tel:+18012055437">
                <span className="ic"><PhoneIc /></span>
                <div>+1 (801) 205-5437<small>Call or text</small></div>
              </a>
              <div className="ci">
                <span className="ic"><Pin /></span>
                <div>Utah, USA<small>Serving modern construction companies</small></div>
              </div>
            </div>
          </div>
          <ConsultationForm className="nl-form reveal d1" source="Contact section form" includeTopic titleTag="h3" prefill={lead} />
        </div>
      </section>

      {/* ---------------- FOOTER ---------------- */}
      <footer className="site-foot">
        <div className="foot-in">
          <div className="foot-top">
            <div className="foot-brand">
              <NailedLogo className="brand-mark foot-mark" nailColor="var(--accent)" />
              <p>Premium construction software that brings your admin, clients and site teams into one connected process.</p>
            </div>
            <div className="foot-links">
              <div className="foot-col">
                <h5>Platform</h5>
                <a href="#platform">Project Intake</a>
                <a href="#platform">Field Operations</a>
                <a href="#platform">Finance &amp; Claims</a>
                <a href="#proof">AR Site Proof</a>
              </div>
              <div className="foot-col">
                <h5>Apps</h5>
                <a href="#mobile">Admin Dashboard</a>
                <a href="#mobile">Client App</a>
                <a href="#mobile">Subcontractor App</a>
              </div>
              <div className="foot-col">
                <h5>Company</h5>
                <a href="#pricing">Pricing</a>
                <a href="#contact">Book Demo</a>
                <a href="#contact">Contact</a>
                <a href="tel:+18012055437">+1 (801) 205-5437</a>
              </div>
            </div>
          </div>
          <div className="foot-bottom">
            <span>© 2026 Nailed. All rights reserved.</span>
            <span>Built for modern builders · Utah, USA</span>
          </div>
        </div>
      </footer>

      <TourModal
        open={tourOpen}
        step={tourStep}
        onPrev={tourPrev}
        onNext={tourNext}
        onDot={gotoTour}
        onClose={closeTour}
        onBook={tourBook}
      />
      <SubscriptionCheckoutModal selection={checkoutSelection} onClose={() => setCheckoutSelection(null)} />
    </main>
  );
}

function ConsultationForm({ className, source, includeTopic = false, titleTag = "h2", prefill = null }) {
  const [values, setValues] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    topic: "",
    details: "",
  });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const Title = titleTag;

  // When a pricing plan CTA is used, seed the message while retaining structured CRM fields.
  useEffect(() => {
    if (prefill && prefill.selectedPlan) {
      const note = `I'm interested in the ${prefill.selectedPlan.name} plan (${prefill.selectedPlan.billing} billing).`;
      setValues((current) => ({ ...current, details: note }));
    }
  }, [prefill]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
  };

  const submitLead = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const response = await fetch(`${apiBaseUrl}/sales/consultation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...values,
          source,
          selectedPlan: prefill?.selectedPlan || undefined,
          ctaIntent: prefill?.ctaIntent || "contact_sales",
          ctaLocation: prefill?.ctaLocation || "contact_form",
          ctaEvent: prefill?.ctaEvent || "consultation_submitted",
        }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.message || "Unable to send request.");
      }

      setValues({ fullName: "", email: "", phoneNumber: "", topic: "", details: "" });
      setStatus({ type: "success", message: "Request sent. We will contact you shortly." });
    } catch (error) {
      setStatus({ type: "error", message: error.message || "Unable to send request." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className={className} onSubmit={submitLead}>
      <Title>Schedule a Free Consultation</Title>
      <input
        aria-label="Full Name"
        name="fullName"
        placeholder="Full Name"
        value={values.fullName}
        onChange={updateField}
        required
      />
      <input
        aria-label="Email"
        name="email"
        type="email"
        placeholder="Email"
        value={values.email}
        onChange={updateField}
        required
      />
      <input
        aria-label="Phone Number"
        name="phoneNumber"
        type="tel"
        placeholder="Phone Number"
        value={values.phoneNumber}
        onChange={updateField}
        required
      />
      {includeTopic && (
        <select aria-label="Select a topic" name="topic" value={values.topic} onChange={updateField}>
          <option value="">Select a topic</option>
          <option>Admin Dashboard</option>
          <option>Client Mobile App</option>
          <option>Subcontractor App</option>
        </select>
      )}
      <textarea
        aria-label="Details"
        name="details"
        placeholder="Details"
        value={values.details}
        onChange={updateField}
      />
      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? "Sending..." : "Get Started Now"}
      </button>
      {status.message && (
        <p className={`form-status ${status.type}`} role="status">
          {status.message}
        </p>
      )}
    </form>
  );
}

export default App;
